import { useEffect, useRef, useState } from 'react';

/**
 * Track document visibility as a boolean that flips on the `visibilitychange`
 * event. Use it to PAUSE background work on a hidden/backgrounded tab.
 *
 * The canonical use is gating a react-query poll so a backgrounded console does
 * not keep hammering the gateway — pass a falsey `refetchInterval` while hidden
 * and leave `refetchIntervalInBackground` unset (its default `false`), so the
 * query resumes (and refetches) the moment the tab is shown again:
 *
 * ```ts
 * const pageVisible = usePageVisible();
 * const { data } = useApiQuery('/live/feed', {
 *   refetchInterval: pageVisible ? 5_000 : false,
 *   refetchIntervalInBackground: false,
 * });
 * ```
 *
 * SSR-safe: with no `document` it reports visible so first paint is never gated.
 */
export function usePageVisible(): boolean {
  const [visible, setVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

export interface UseVisibilityGatedIntervalOptions {
  /**
   * When false, the interval is not scheduled at all (mirrors an `enabled`
   * flag). Defaults to true.
   */
  enabled?: boolean;
  /**
   * Fire the callback once immediately when the tab becomes visible again after
   * having been hidden, so a re-shown tab shows fresh data without waiting a
   * full period. Defaults to true.
   */
  runOnVisible?: boolean;
}

/**
 * `setInterval` that automatically PAUSES while the tab is hidden and RESUMES
 * when it becomes visible again — the raw-timer analogue of gating a
 * react-query `refetchInterval` with {@link usePageVisible}. A backgrounded tab
 * stops firing the callback entirely (no wasted polls / re-renders); on return
 * it optionally fires once immediately then continues on cadence.
 *
 * The latest `callback` is always invoked without needing to be in the deps, so
 * callers can pass an inline closure without tearing down the timer each render.
 *
 * @param callback fired every `intervalMs` while the tab is visible
 * @param intervalMs cadence in ms; a non-positive value disables the timer
 */
export function useVisibilityGatedInterval(
  callback: () => void,
  intervalMs: number,
  options: UseVisibilityGatedIntervalOptions = {}
): void {
  const { enabled = true, runOnVisible = true } = options;
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled || intervalMs <= 0 || typeof document === 'undefined') return;

    let timerId: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const start = () => {
      if (timerId === null) {
        timerId = setInterval(() => savedCallback.current(), intervalMs);
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        if (runOnVisible) savedCallback.current();
        start();
      }
    };

    // Only run while visible; if we mount on a hidden tab, wait for it to show.
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [enabled, intervalMs, runOnVisible]);
}
